import { Navigate, Route, Routes } from 'react-router-dom';
import { Spinner } from './components/Spinner';
import { Layout } from './components/Layout';
import { useAuth } from './lib/auth';
import type { Role } from '@shared/types';
import { LandingPage } from './pages/LandingPage';
import { LoginPage } from './pages/LoginPage';
import { DocumentationPage } from './pages/DocumentationPage';
import { DashboardPage } from './pages/DashboardPage';
import { LinksPage } from './pages/LinksPage';
import { LinkDetailPage } from './pages/LinkDetailPage';
import { NewLinkPage } from './pages/NewLinkPage';
import { CampaignsPage } from './pages/CampaignsPage';
import { CampaignDetailPage } from './pages/CampaignDetailPage';
import { SettingsPage } from './pages/SettingsPage';
import { NotFoundPage } from './pages/NotFoundPage';
import { AdminDashboardPage } from './pages/admin/AdminDashboardPage';
import { AdminUsersPage } from './pages/admin/AdminUsersPage';
import { AdminOrgsPage } from './pages/admin/AdminOrgsPage';
import { AdminLinksPage } from './pages/admin/AdminLinksPage';
import { AdminCampaignsPage } from './pages/admin/AdminCampaignsPage';
import { AdminAnalyticsPage } from './pages/admin/AdminAnalyticsPage';
import { AdminAuditPage } from './pages/admin/AdminAuditPage';
import { AdminSettingsPage } from './pages/admin/AdminSettingsPage';

function ProtectedRoute({
  children,
  minRole,
}: {
  children: JSX.Element;
  minRole?: Role;
}) {
  const { user, loading } = useAuth();
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Spinner size={28} />
      </div>
    );
  }
  if (!user) return <Navigate to="/login" replace />;
  const ranks: Record<Role, number> = {
    viewer: 1,
    member: 2,
    org_admin: 3,
    org_owner: 4,
    admin: 5,
    super_admin: 6,
  };
  if (minRole && ranks[user.role] < ranks[minRole]) {
    return <Navigate to="/app" replace />;
  }
  return children;
}

export function App() {
  return (
    <Routes>
      <Route path="/" element={<LandingPage />} />
      <Route path="/docs" element={<DocumentationPage />} />
      <Route path="/login" element={<LoginPage />} />

      <Route
        path="/app"
        element={
          <ProtectedRoute>
            <Layout variant="customer" />
          </ProtectedRoute>
        }
      >
        <Route index element={<DashboardPage />} />
        <Route path="links" element={<LinksPage />} />
        <Route path="links/new" element={<NewLinkPage />} />
        <Route path="links/:id" element={<LinkDetailPage />} />
        <Route path="campaigns" element={<CampaignsPage />} />
        <Route path="campaigns/:id" element={<CampaignDetailPage />} />
        <Route path="settings" element={<SettingsPage />} />
      </Route>

      <Route
        path="/admin"
        element={
          <ProtectedRoute minRole="admin">
            <Layout variant="admin" />
          </ProtectedRoute>
        }
      >
        <Route index element={<AdminDashboardPage />} />
        <Route path="users" element={<AdminUsersPage />} />
        <Route path="orgs" element={<AdminOrgsPage />} />
        <Route path="links" element={<AdminLinksPage />} />
        <Route path="campaigns" element={<AdminCampaignsPage />} />
        <Route path="analytics" element={<AdminAnalyticsPage />} />
        <Route path="audit" element={<AdminAuditPage />} />
        <Route path="settings" element={<AdminSettingsPage />} />
      </Route>

      <Route path="*" element={<NotFoundPage />} />
    </Routes>
  );
}
